/**
 * Reports – Patient Wise Report.
 * User types Unique ID and clicks Search; screen lists all services on that bill (self + outsource).
 * Each row exposes View / Download for the underlying result:
 *   - self-lab rows: hit result-print (PDF) for the aggregated self result on the bill.
 *   - outsource rows: fetch the uploaded S3 file(s) via aws-s3/url presigned link.
 */
import React, { useState } from "react";
import {
  Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, Button, Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import { isOnlyServiceName } from "../../Utility/ServiceNameFormat";
import "../../Styles/Configuration.css";

const InfoField = ({ label, value }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Typography sx={{ fontSize: "0.8vw", color: "#666" }}>{label}</Typography>
    <Tooltip title={value || "-"} placement="top" arrow>
      <Typography
        sx={{
          fontSize: "0.95vw", fontWeight: 600, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis", textTransform: label === "Patient Name" ? "uppercase" : "none",
        }}
      >
        {value || "-"}
      </Typography>
    </Tooltip>
  </Box>
);

const statusChip = (row) => {
  if (row.laboratory === "outsource") {
    const s = row.outsource_status || (row.result_status ? row.result_status.replace(/_/g, " ") : "Pending");
    return <Chip size="small" label={s} sx={{ textTransform: "capitalize", fontSize: "0.75vw" }} />;
  }
  const s = row.result_status ? row.result_status.replace(/_/g, " ") : "Pending";
  const color = row.result_status === "approved" ? "success" : "default";
  return <Chip size="small" label={s} color={color} sx={{ textTransform: "capitalize", fontSize: "0.75vw" }} />;
};

export default function PatientWiseReport() {
  const [nxtInput, setNxtInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState(null);
  const [bill, setBill] = useState(null);
  const [bills, setBills] = useState([]);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const runSearch = () => {
    const v = (nxtInput || "").trim();
    if (!v) {
      setMsg({ err: true, errText: "Enter Unique ID to search", ok: false, okText: "" });
      return;
    }
    setLoading(true);
    setPatient(null); setBill(null); setBills([]); setRows([]);
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_REPORTS_PATIENT_WISE}?nxt_id=${encodeURIComponent(v)}`)
      .then((res) => {
        if (res?.data?.status === "success") {
          const d = res.data.data || {};
          setPatient(d.patient || null);
          setBill(d.bill || null);
          setBills(Array.isArray(d.bills) ? d.bills : (d.bill ? [d.bill] : []));
          setRows((d.services || []).map((s, i) => ({ id: `${s.bill_id}-${s.bill_line_item_id}-${i}`, ...s })));
        } else {
          setMsg({ err: true, errText: res?.data?.message || "No data", ok: false, okText: "" });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message || "Failed to load", ok: false, okText: "" });
      });
  };

  const openBlob = (blob, filename, download) => {
    const url = URL.createObjectURL(blob);
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.download = filename || "report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const openSelfReport = (row, download) => {
    if (!row.result_detail_id) return;
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PRINT}?result_id=${row.result_detail_id}&export_type=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        openBlob(file, `${bill?.bill_number || "report"}-${row.service_name}.pdf`, download);
      })
      .catch((err) => setMsg({ err: true, errText: err?.response?.data?.message || err?.message || "Unable to fetch report", ok: false, okText: "" }));
  };

  const openOutsourceDocs = (row, download) => {
    const docs = row.result_documents || [];
    if (!docs.length) return;
    docs.forEach((doc, i) => {
      const key = doc.document_file;
      if (!key) return;
      RestAPIService.getAll(`${Serviceurls.AWS_URL_GET}?doc_key=${encodeURIComponent(key)}`)
        .then((res) => {
          const presigned = typeof res?.data?.data === "string" ? res.data.data : (res?.data?.data?.url || res?.data?.url);
          if (!presigned) {
            setMsg({ err: true, errText: "Unable to fetch document URL", ok: false, okText: "" });
            return;
          }
          if (download) {
            const a = document.createElement("a");
            a.href = presigned;
            a.download = doc.document_name || `attachment-${i + 1}`;
            a.target = "_blank"; a.rel = "noopener";
            document.body.appendChild(a); a.click(); a.remove();
          } else {
            window.open(presigned, "_blank", "noopener,noreferrer");
          }
        })
        .catch((err) => setMsg({ err: true, errText: err?.response?.data?.message || err?.message || "Failed to open document", ok: false, okText: "" }));
    });
  };

  const handleView = (row) => (row.laboratory === "outsource" ? openOutsourceDocs(row, false) : openSelfReport(row, false));
  const handleDownload = (row) => (row.laboratory === "outsource" ? openOutsourceDocs(row, true) : openSelfReport(row, true));

  const columns = [
    { field: "sno", headerName: "S.No", flex: 0.35, minWidth: 60, sortable: false },
    {
      field: "bill_number", headerName: "Bill Number", flex: 0.8, minWidth: 120,
      renderCell: (p) => (
        <Tooltip title={p.row.bill_number || "-"} placement="top" arrow>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {p.row.bill_number || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "service_name", headerName: "Service", flex: 1.4, minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
          <Tooltip title={p.row.service_name || "-"} placement="top" arrow>
            <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {p.row.service_name || "-"}
            </Typography>
          </Tooltip>
          {p.row.service_head && !isOnlyServiceName() && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {p.row.service_head}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: "specimen", headerName: "Specimen", flex: 0.9, minWidth: 130 },
    {
      field: "sample_id", headerName: "Biopsy Number", flex: 0.9, minWidth: 130,
      renderCell: (p) => (
        <Typography variant="body2">{p.row.sample_id || "-"}</Typography>
      ),
    },
    {
      field: "laboratory", headerName: "Lab Type", flex: 0.7, minWidth: 110,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Chip size="small"
            label={p.row.laboratory === "outsource" ? "Outsource" : "Self"}
            color={p.row.laboratory === "outsource" ? "warning" : "primary"} variant="outlined"
            sx={{ fontSize: "0.75vw" }}
          />
          {p.row.laboratory === "outsource" && p.row.outsource_lab_name && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{p.row.outsource_lab_name}</Typography>
          )}
        </Box>
      ),
    },
    { field: "status", headerName: "Status", flex: 0.9, minWidth: 130, sortable: false, renderCell: (p) => statusChip(p.row) },
    {
      field: "actions", headerName: "Actions", sortable: false, flex: 0.8, minWidth: 120,
      align: "center", headerAlign: "center",
      renderCell: (p) => {
        const enabled = !!p.row.has_report;
        const tip = enabled ? "" : "No report available yet";
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={tip || "View"}>
              <span>
                <IconButton size="small" color="primary" disabled={!enabled} onClick={() => handleView(p.row)}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={tip || "Download"}>
              <span>
                <IconButton size="small" color="primary" disabled={!enabled} onClick={() => handleDownload(p.row)}>
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Paper elevation={1} sx={{ p: 1.2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small" label="Unique ID" placeholder="e.g. NXT00001"
            value={nxtInput}
            onChange={(e) => setNxtInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            sx={{ minWidth: "16vw" }}
            inputProps={{ maxLength: 20 }}
          />
          <Button variant="contained" size="small" startIcon={<SearchOutlinedIcon />} onClick={runSearch}
            sx={{ textTransform: "capitalize" }}>
            Search
          </Button>
        </Stack>
      </Paper>

      {(patient || bill) && (
        <Paper elevation={1} sx={{ p: 1.2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }} useFlexGap flexWrap="wrap">
            <InfoField label="Patient Name" value={patient?.name} />
            <InfoField label="Mobile" value={patient?.mobile} />
            <InfoField label="Age / Gender" value={[patient?.age, patient?.gender].filter(Boolean).join(" / ")} />
            <InfoField
              label={bills.length > 1 ? "Bill Numbers" : "Bill Number"}
              value={bills.length > 1 ? bills.map((b) => b.bill_number).filter(Boolean).join(", ") : bill?.bill_number}
            />
            <InfoField label="Unique ID" value={bill?.nxt_id} />
            <InfoField
              label={bills.length > 1 ? "Latest Bill Date" : "Bill Date"}
              value={bills.length > 1 ? bills[bills.length - 1]?.bill_date : bill?.bill_date}
            />
          </Stack>
        </Paper>
      )}

      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          rowHeight={54}
          columnHeaderHeight={42}
          pageSizeOptions={[10, 20, 30]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">Enter Unique ID and click Search</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>

      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
